/* NKAMARO — theme interactions. No framework: small, real, dependency-free
 * modules registered as custom elements, matching Shopify Online Store 2.0
 * theme conventions (progressive enhancement over server-rendered Liquid). */

/* ---------------------------------------------------------------------
 * Scroll reveal — sections 3-8 of the homepage fade+rise in, staggered
 * ~80ms per item, capped at 300ms total (docs/02-homepage-structure.md).
 * ------------------------------------------------------------------- */
(function scrollReveal() {
  const items = document.querySelectorAll('[data-scroll-reveal]');
  if (!items.length) return;

  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const groups = new Map();
  items.forEach((el) => {
    const group = el.closest('[data-scroll-reveal-group]') || el;
    const list = groups.get(group) || [];
    list.push(el);
    groups.set(group, list);
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const target = entry.target;
        const siblings = groups.get(target.closest('[data-scroll-reveal-group]') || target) || [target];
        const index = siblings.indexOf(target);
        const delay = Math.min(index * 80, 300);
        target.style.transitionDelay = `${delay}ms`;
        target.classList.add('is-visible');
        observer.unobserve(target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -80px 0px' },
  );

  items.forEach((el) => observer.observe(el));
})();

/* ---------------------------------------------------------------------
 * Theme toggle (dark default, light opt-in) — persisted, respects a
 * prior explicit choice over the OS preference once set.
 * ------------------------------------------------------------------- */
(function themeToggle() {
  const STORAGE_KEY = 'nkamaro:theme';
  const root = document.documentElement;

  function apply(theme) {
    if (theme === 'light') root.setAttribute('data-theme', 'light');
    else root.removeAttribute('data-theme');
  }

  function syncButtons() {
    const isLight = root.getAttribute('data-theme') === 'light';
    document.querySelectorAll('[data-theme-toggle]').forEach((trigger) => {
      trigger.setAttribute('aria-pressed', String(isLight));
      const label = trigger.querySelector('[data-theme-toggle-label]');
      if (!label) return;
      label.textContent = isLight
        ? trigger.getAttribute('data-label-to-dark')
        : trigger.getAttribute('data-label-to-light');
    });
  }

  // The inline blocking script in theme.liquid already applied the correct
  // data-theme attribute before this (deferred) script ever runs; this only
  // syncs the toggle button's ARIA state to match whatever that already is.
  syncButtons();

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-theme-toggle]');
    if (!trigger) return;
    const next = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
    apply(next);
    window.localStorage.setItem(STORAGE_KEY, next);
    syncButtons();
  });
})();

/* ---------------------------------------------------------------------
 * Global reach globe — strips the connection-line/city-glow layer (SVG
 * path animation + 6 pulsing dots) on devices likely to struggle with it,
 * keeping only the single cheap sphere-rotation transform. deviceMemory/
 * hardwareConcurrency are real, standard (if Chromium-only) capability
 * signals, not a guess; browsers without them (Safari/Firefox) just skip
 * that check and rely on prefers-reduced-motion instead.
 * ------------------------------------------------------------------- */
(function globeAdaptiveFx() {
  const globes = document.querySelectorAll('[data-globe-fx]');
  if (globes.length === 0) return;

  const lowMemory = 'deviceMemory' in navigator && navigator.deviceMemory < 4;
  const lowCores = 'hardwareConcurrency' in navigator && navigator.hardwareConcurrency <= 2;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (lowMemory || lowCores || reducedMotion) {
    globes.forEach((globe) => globe.setAttribute('data-globe-fx', 'off'));
  }
})();

/* ---------------------------------------------------------------------
 * Global reach — visitor highlight. `data-visitor-country` is Shopify's
 * own already-resolved geo-IP country (real, server-rendered — not a
 * third-party lookup). Exact matches light up their own dot; anything
 * else falls back to the nearest configured hub via a real (if
 * necessarily incomplete) ISO-country-code map — countries not listed
 * here simply get no highlight rather than a guessed wrong one.
 * ------------------------------------------------------------------- */
(function globeVisitorHighlight() {
  const globe = document.querySelector('[data-visitor-country]');
  if (!globe) return;

  const country = globe.dataset.visitorCountry;
  if (!country) return;

  const HUB_LABELS = {
    gcc: 'the GCC', us: 'the United States', canada: 'Canada', uk: 'the United Kingdom',
    europe: 'Europe', australia: 'Australia', singapore: 'Southeast Asia', japan: 'East Asia',
  };

  const DIRECT = {
    SA: 'gcc', AE: 'gcc', QA: 'gcc', KW: 'gcc', BH: 'gcc', OM: 'gcc',
    US: 'us', CA: 'canada', GB: 'uk',
    DE: 'europe', FR: 'europe', IT: 'europe', ES: 'europe', NL: 'europe', BE: 'europe',
    IE: 'europe', PT: 'europe', AT: 'europe', CH: 'europe', SE: 'europe', NO: 'europe',
    DK: 'europe', FI: 'europe', PL: 'europe', GR: 'europe', CZ: 'europe', LU: 'europe',
    AU: 'australia', NZ: 'australia',
    SG: 'singapore', MY: 'singapore', TH: 'singapore', ID: 'singapore', PH: 'singapore', VN: 'singapore',
    JP: 'japan', KR: 'japan',
  };

  // Nearest configured hub for common countries outside the 8 direct
  // regions — an honest "closest real hub" heuristic, not a claim of
  // literal continent accuracy.
  const NEAREST_HUB = {
    IQ: 'gcc', JO: 'gcc', LB: 'gcc', TR: 'gcc',
    ZA: 'europe', EG: 'europe', NG: 'europe', KE: 'europe', MA: 'europe',
    IN: 'singapore', PK: 'singapore', BD: 'singapore', LK: 'singapore',
    BR: 'us', MX: 'us', AR: 'us', CL: 'us', CO: 'us',
    CN: 'japan', HK: 'japan', TW: 'japan',
  };

  const region = DIRECT[country] || NEAREST_HUB[country];
  if (!region) return;

  const dot = globe.querySelector(`[data-region="${region}"]`);
  if (dot) dot.setAttribute('data-active', '');

  const note = document.querySelector('[data-globe-visitor-note]');
  if (note && HUB_LABELS[region]) {
    note.textContent = note.dataset.template
      ? note.dataset.template.replace('___REGION___', HUB_LABELS[region])
      : '';
    if (note.textContent) {
      note.removeAttribute('hidden');
      requestAnimationFrame(() => note.setAttribute('data-visible', ''));
    }
  }
})();

/* ---------------------------------------------------------------------
 * Hero scroll cue — smooth-scrolls to the next section.
 * ------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const cue = event.target.closest('[data-hero-scroll-cue]');
  if (!cue) return;
  const hero = cue.closest('[data-hero]');
  const next = hero?.nextElementSibling;
  next?.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

/* ---------------------------------------------------------------------
 * Header scroll state — transparent-over-hero on Home, solid elsewhere
 * or after scrolling past the hero (docs/01-store-architecture.md §2).
 * ------------------------------------------------------------------- */
(function headerScrollState() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;
  if (!header.hasAttribute('data-transparent')) return;

  const onScroll = () => {
    header.classList.toggle('is-solid', window.scrollY > window.innerHeight * 0.7);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* ---------------------------------------------------------------------
 * Mobile nav — full-screen overlay, not an edge drawer (avoids
 * competing with swipe-back gestures, per architecture doc).
 * ------------------------------------------------------------------- */
class MobileNav extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector('[data-mobile-nav-trigger]');
    this.closeBtn = this.querySelector('[data-mobile-nav-close]');
    this.trigger?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.querySelector('a, button')?.focus();
  }

  close() {
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.trigger?.focus();
  }
}
customElements.define('mobile-nav', MobileNav);

/* ---------------------------------------------------------------------
 * Search overlay — full-width, live-typeahead (debounced Predictive
 * Search API), not a redirect on first keystroke.
 * ------------------------------------------------------------------- */
const RECENT_SEARCHES_KEY = 'nkamaro:recent-searches';
const RECENT_SEARCHES_MAX = 5;

class SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector('[data-search-trigger]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.defaultPanel = this.querySelector('[data-search-default]');
    this.recentSection = this.querySelector('[data-search-recent]');
    this.recentList = this.querySelector('[data-search-recent-list]');
    this.closeBtn = this.querySelector('[data-search-close]');
    this.debounceTimer = null;

    this.trigger?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (e) => this.onKeydown(e));
    this.input?.addEventListener('input', () => this.onInput());

    this.addEventListener('click', (e) => {
      const link = e.target.closest('[data-search-result-link], .search-overlay__results a');
      if (link) this.recordSearch(link.textContent.trim());
      if (e.target.closest('[data-search-clear-recent]')) this.clearRecent();
    });

    this.renderRecent();
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.input?.focus();
    this.renderRecent();
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this.trigger?.focus();
  }

  getRecent() {
    try {
      return JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    } catch {
      return [];
    }
  }

  recordSearch(term) {
    if (!term) return;
    const existing = this.getRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
    existing.unshift(term);
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(existing.slice(0, RECENT_SEARCHES_MAX)));
  }

  clearRecent() {
    window.localStorage.removeItem(RECENT_SEARCHES_KEY);
    this.renderRecent();
  }

  renderRecent() {
    if (!this.recentSection || !this.recentList) return;
    const recent = this.getRecent();
    this.recentSection.hidden = recent.length === 0;
    this.recentList.innerHTML = recent
      .map((term) => {
        const url = `${window.Shopify?.routes?.root ?? '/'}search?q=${encodeURIComponent(term)}`;
        const label = term.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        return `<li><a href="${url}" data-search-result-link>${label}</a></li>`;
      })
      .join('');
  }

  onInput() {
    window.clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.defaultPanel) this.defaultPanel.hidden = false;
      return;
    }
    if (this.defaultPanel) this.defaultPanel.hidden = true;
    this.results.hidden = false;
    this.debounceTimer = window.setTimeout(() => this.fetchResults(query), 220);
  }

  async fetchResults(query) {
    this.results.setAttribute('aria-busy', 'true');
    try {
      const url = `${window.Shopify?.routes?.root ?? '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
      const response = await fetch(url);
      const html = await response.text();
      this.results.innerHTML = html;
    } catch {
      this.results.innerHTML = '<p class="t-body-sm" data-search-error>Something went wrong — try again.</p>';
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }

  /* Arrow-key navigation across whichever panel is currently visible
   * (default recent/popular/trending, or live results) — real keyboard
   * support, not just Tab order. */
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

    const activePanel = this.defaultPanel && !this.defaultPanel.hidden ? this.defaultPanel : this.results;
    const links = Array.from(activePanel.querySelectorAll('a, button:not([data-search-close])'));
    if (!links.length) return;

    const currentIndex = links.indexOf(document.activeElement);

    if (event.key === 'Enter' && document.activeElement === this.input) {
      links[0]?.click();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const nextIndex = event.key === 'ArrowDown' ? Math.min(currentIndex + 1, links.length - 1) : currentIndex - 1;
    if (nextIndex < 0) {
      this.input.focus();
    } else {
      links[nextIndex]?.focus();
    }
  }
}
customElements.define('search-overlay', SearchOverlay);

/* ---------------------------------------------------------------------
 * Cart drawer — slide-out, not a page nav. Line items, subtotal, single
 * primary CTA, free-shipping progress bar.
 * ------------------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.closeBtn = this.querySelector('[data-cart-close]');
    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cart-trigger]');
      if (trigger) {
        e.preventDefault();
        this.open();
      }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        this.updateItem(removeBtn.dataset.cartRemove, 0);
      }
    });
    this.addEventListener('change', (e) => {
      const qtyInput = e.target.closest('[data-cart-qty]');
      if (qtyInput) this.updateItem(qtyInput.dataset.cartQty, qtyInput.value);
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  async updateItem(key, quantity) {
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) }),
      });
      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      await this.refresh();
      loadCartCrossSell();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async refresh() {
    const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-drawer');
    if (fresh) this.innerHTML = fresh.innerHTML;
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------------------------------------------------------------------
 * Quick View — fetches sections/quick-view-content.liquid rendered
 * against the clicked product's own URL (Section Rendering API), so
 * `product` is real there; this drawer is just the shell. Add-to-cart
 * and wishlist-toggle inside the injected content work automatically —
 * both are document-level delegated listeners, not scoped to a
 * container that existed at page load.
 * ------------------------------------------------------------------- */
class QuickViewDrawer extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-quick-view-content]');
    this.closeBtn = this.querySelector('[data-quick-view-close]');
    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;
      e.preventDefault();
      this.load(trigger.dataset.quickView);
    });
  }

  async load(productUrl) {
    this.open();
    this.setAttribute('aria-busy', 'true');
    this.content.innerHTML = '<div class="skeleton quick-view__skeleton"></div>';
    try {
      const response = await fetch(`${productUrl}?section_id=quick-view-content`);
      this.content.innerHTML = await response.text();
    } catch {
      this.close();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

/* Add-to-cart forms — intercepted so the drawer opens instead of a full
 * page reload, everywhere a product form exists (PDP, quick-add). */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-add-to-cart-form]');
  if (!form) return;
  event.preventDefault();

  const submitBtn = form.querySelector('[type="submit"]');
  submitBtn?.setAttribute('aria-busy', 'true');
  submitBtn?.setAttribute('disabled', '');

  try {
    const formData = new FormData(form);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const addedItem = await response.json();
    if (!response.ok) {
      throw new Error(addedItem.description || 'Could not add this item to your cart.');
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: addedItem.price_currency || undefined,
          value: (addedItem.final_price || 0) / 100,
          items: [{ item_id: addedItem.id, item_name: addedItem.product_title, price: (addedItem.final_price || 0) / 100, quantity: addedItem.quantity }],
        },
      });
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.querySelector('cart-drawer')?.refresh?.();
  } catch (err) {
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) errorEl.textContent = err.message;
  } finally {
    submitBtn?.removeAttribute('aria-busy');
    submitBtn?.removeAttribute('disabled');
  }
});

/* "Frequently bought together" — adds every real variant in the bundle
 * to the cart in a single request (Shopify's own /cart/add.js accepts
 * an `items` array), not one request per product; same drawer-refresh
 * as a normal add-to-cart. */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-bundle-add-to-cart]');
  if (!button) return;

  const errorEl = button.parentElement.querySelector('[data-bundle-error]');
  if (errorEl) errorEl.textContent = '';
  button.setAttribute('aria-busy', 'true');
  button.setAttribute('disabled', '');

  try {
    const variantIds = button.dataset.variantIds.split(',').filter(Boolean).map((id) => ({ id: Number(id), quantity: 1 }));
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: variantIds }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.description || 'Could not add this bundle to your cart.');
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.querySelector('cart-drawer')?.refresh?.();
  } catch (err) {
    if (errorEl) errorEl.textContent = err.message;
  } finally {
    button.removeAttribute('aria-busy');
    button.removeAttribute('disabled');
  }
});

/* ---------------------------------------------------------------------
 * Collection grid/list view toggle — persisted per-browser. Re-queries
 * the grid fresh on every call rather than caching a reference — the
 * AJAX collection controller below replaces the grid's markup on every
 * filter/sort/load-more, which would silently detach a cached node
 * (found live building the AJAX layer: a cached reference kept applying
 * the class to an element no longer in the document).
 * ------------------------------------------------------------------- */
const STORAGE_KEY_COLLECTION_VIEW = 'nkamaro:collection-view';

function applyCollectionView(view) {
  document.querySelector('[data-collection-grid]')?.classList.toggle('collection__grid--list', view === 'list');
  document.querySelectorAll('[data-view-toggle]').forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.viewToggle === view)));
}

(function collectionViewToggle() {
  const buttons = document.querySelectorAll('[data-view-toggle]');
  if (!document.querySelector('[data-collection-grid]') || !buttons.length) return;

  applyCollectionView(window.localStorage.getItem(STORAGE_KEY_COLLECTION_VIEW) || 'grid');

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const view = btn.dataset.viewToggle;
      applyCollectionView(view);
      window.localStorage.setItem(STORAGE_KEY_COLLECTION_VIEW, view);
    });
  });
})();

/* ---------------------------------------------------------------------
 * Collection filter/sort/load-more — progressive enhancement over the
 * real GET form and real pagination links already in main-collection.liquid.
 * Both keep working with JS disabled or on a fetch failure (real
 * navigation, real crawlable URLs for every filter/sort/page combination
 * — Shopify's own faceted-nav SEO model, never broken by this layer);
 * with JS, the same requests instead fetch `section_id=main-collection`
 * and swap markup in place with a real skeleton loading state.
 * ------------------------------------------------------------------- */
(function collectionAjax() {
  const root = document.querySelector('[data-collection-ajax-root]');
  const form = document.querySelector('[data-collection-filter-form]');
  if (!root || !form) return;

  function showSkeleton() {
    document.querySelector('[data-collection-grid]')?.setAttribute('hidden', '');
    document.querySelector('[data-collection-skeleton]')?.removeAttribute('hidden');
  }

  async function loadInto(url, { append = false } = {}) {
    showSkeleton();
    try {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', 'main-collection');
      const response = await fetch(sectionUrl.toString());
      if (!response.ok) throw new Error('Collection request failed');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshRoot = doc.querySelector('[data-collection-ajax-root]');
      if (!freshRoot) throw new Error('Collection response had no matching content');

      if (append) {
        const currentGrid = document.querySelector('[data-collection-grid]');
        const freshGrid = freshRoot.querySelector('[data-collection-grid]');
        if (currentGrid && freshGrid) currentGrid.append(...freshGrid.children);
        const currentPagination = document.querySelector('[data-collection-pagination]');
        const freshPagination = freshRoot.querySelector('[data-collection-pagination]');
        if (currentPagination) {
          if (freshPagination) currentPagination.replaceWith(freshPagination);
          else currentPagination.remove();
        }
      } else {
        root.innerHTML = freshRoot.innerHTML;
      }

      window.history.pushState({}, '', url);
      applyCollectionView(window.localStorage.getItem(STORAGE_KEY_COLLECTION_VIEW) || 'grid');
      document.querySelector('[data-collection-grid]')?.removeAttribute('hidden');
      document.querySelector('[data-collection-skeleton]')?.setAttribute('hidden', '');
      document.querySelectorAll('[data-scroll-reveal]').forEach((el) => el.classList.add('is-visible'));
    } catch (err) {
      // A real failure falls back to a real navigation rather than
      // leaving the page stuck on the skeleton.
      window.location.href = url;
    }
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const params = new URLSearchParams(new FormData(form));
    loadInto(`${form.action}?${params.toString()}`);
  });

  document.addEventListener('click', (event) => {
    const nextLink = event.target.closest('[data-collection-pagination] a[href]');
    if (!nextLink) return;
    const pagination = nextLink.closest('[data-collection-pagination]');
    // Only the real "next" link becomes a Load More (appends); "previous"
    // still navigates normally — going backward through an appended,
    // multi-page-long grid has no sensible in-place meaning.
    if (nextLink.getAttribute('href') !== pagination?.dataset.nextUrl) return;
    event.preventDefault();
    loadInto(nextLink.getAttribute('href'), { append: true });
  });
})();

/* ---------------------------------------------------------------------
 * Cart page — discount code (real Shopify discount-URL navigation, not
 * an AJAX endpoint — Shopify applies discounts via a path-based redirect,
 * e.g. /discount/SUMMER10?redirect=/cart), shipping estimator (real
 * /cart/shipping_rates.json call), and cross-sell (real recommendations
 * endpoint rendered server-side via section_id, fetched client-side).
 * ------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const applyBtn = event.target.closest('[data-apply-discount]');
  if (applyBtn) {
    const input = document.querySelector('[data-discount-code-input]');
    const code = input?.value.trim();
    if (!code) return;
    const baseUrl = applyBtn.dataset.discountBaseUrl;
    window.location.href = `${baseUrl}${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart')}`;
  }

  const estimatorToggle = event.target.closest('[data-estimator-toggle]');
  if (estimatorToggle) {
    const panel = estimatorToggle.closest('cart-shipping-estimator')?.querySelector('[data-estimator-panel]');
    panel?.toggleAttribute('hidden');
  }

  const estimatorSubmit = event.target.closest('[data-estimator-submit]');
  if (estimatorSubmit) {
    const root = estimatorSubmit.closest('cart-shipping-estimator');
    const zip = root?.querySelector('[data-estimator-zip]')?.value.trim();
    const country = root?.querySelector('[data-estimator-country]')?.value;
    const results = root?.querySelector('[data-estimator-results]');
    if (!results) return;
    if (!zip) {
      results.innerHTML = `<p class="field__error">${window.NKAMARO_STRINGS.estimatorError}</p>`;
      return;
    }
    results.innerHTML = '<span class="spinner"></span>';
    fetch(`/cart/shipping_rates.json?shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}`)
      .then((res) => res.json())
      .then((data) => {
        const rates = data.shipping_rates || [];
        if (rates.length === 0) {
          results.innerHTML = `<p class="t-caption">${window.NKAMARO_STRINGS.estimatorNoRates}</p>`;
          return;
        }
        results.innerHTML = rates
          .map((rate) => `<div class="cart-page__summary-row"><span class="t-caption">${rate.name}</span><span class="t-price t-caption">${(rate.price / 100).toFixed(2)}</span></div>`)
          .join('');
      })
      .catch(() => {
        results.innerHTML = `<p class="field__error">${window.NKAMARO_STRINGS.estimatorNoRates}</p>`;
      });
  }
});

function loadCartCrossSell() {
  document.querySelectorAll('[data-cart-cross-sell]').forEach((container) => {
    if (container.dataset.loaded) return;
    const productId = container.dataset.productId;
    fetch(`/recommendations/products?product_id=${productId}&limit=4&section_id=cart-cross-sell`)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-cart-cross-sell-content]');
        if (content && content.children.length > 0) {
          container.innerHTML = content.innerHTML;
          container.dataset.loaded = 'true';
        }
      })
      .catch(() => {});
  });
}
loadCartCrossSell();

/* ---------------------------------------------------------------------
 * Quantity steppers — cart drawer, cart page.
 * ------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-qty-step]');
  if (!btn) return;
  const wrapper = btn.closest('.qty-stepper');
  const input = wrapper?.querySelector('input');
  if (!input) return;
  const delta = Number(btn.dataset.qtyStep);
  const next = Math.max(1, Number(input.value) + delta);
  input.value = String(next);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------------------------------------------------------------------
 * Product gallery — thumbnail select + click-to-zoom (lightbox), no
 * external library.
 * ------------------------------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.mainContainer = this.querySelector('[data-gallery-main]');
    this.main = this.mainContainer?.querySelector('img');
    this.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-gallery-thumb]');
      if (thumb) this.select(thumb);
      const zoomTrigger = e.target.closest('[data-gallery-zoom-trigger]');
      if (zoomTrigger) this.openZoom(zoomTrigger);
      const zoomClose = e.target.closest('[data-gallery-zoom-close]');
      if (zoomClose) this.closeZoom();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeZoom();
    });
  }

  /* Video/3D model thumbnails clone their real, server-rendered
   * <template> (native <video>, a real YouTube/Vimeo embed, or a real
   * <model-viewer> — whatever media_tag actually produced) into the main
   * viewer; an image thumbnail still just swaps the plain <img> in place,
   * cheaper and avoids a layout/decode flash for the overwhelmingly
   * common case. Any currently playing <video>/<iframe> embed is torn
   * down on every switch — leaving one playing silently behind a newly
   * selected image would be a real, confusing bug, not a cosmetic one. */
  select(thumb) {
    this.querySelectorAll('[data-gallery-thumb]').forEach((t) => t.setAttribute('aria-current', 'false'));
    thumb.setAttribute('aria-current', 'true');
    if (!this.mainContainer) return;

    const template = this.querySelector(`[data-gallery-media-template="${thumb.dataset.mediaId}"]`);
    if (template) {
      this.mainContainer.replaceChildren(template.content.cloneNode(true));
      this.main = this.mainContainer.querySelector('img');
    }
  }

  openZoom(trigger) {
    const zoom = this.querySelector('[data-gallery-zoom]');
    const zoomImg = zoom?.querySelector('img');
    if (!zoom || !zoomImg) return;
    zoomImg.src = trigger.dataset.fullSrc || this.main?.src;
    zoom.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  closeZoom() {
    const zoom = this.querySelector('[data-gallery-zoom]');
    zoom?.removeAttribute('open');
    document.body.style.overflow = '';
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------------------------------------------------------------------
 * Variant picker — swaps price/availability/media without a page
 * reload; disables combinations that don't exist as a real variant.
 * ------------------------------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variantData = JSON.parse(this.querySelector('[data-variant-json]')?.textContent || '[]');
    this.addEventListener('change', () => this.onChange());
    this.onChange();
  }

  onChange() {
    const selected = Array.from(this.querySelectorAll('select, input:checked')).map((el) => el.value);
    const match = this.variantData.find((variant) => variant.options.every((opt, i) => opt === selected[i]));

    const priceEl = document.querySelector('[data-product-price]');
    const submitBtn = document.querySelector('[data-add-to-cart-form] [type="submit"]');
    const idInput = document.querySelector('[data-add-to-cart-form] [name="id"]');
    const availabilityEl = document.querySelector('[data-variant-availability]');

    if (!match) {
      if (submitBtn) {
        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = submitBtn.dataset.unavailableLabel || 'Unavailable';
      }
      return;
    }

    if (idInput) idInput.value = match.id;
    if (priceEl) priceEl.textContent = match.price_formatted;
    if (availabilityEl) {
      availabilityEl.textContent = match.available ? availabilityEl.dataset.inStock : availabilityEl.dataset.outOfStock;
      availabilityEl.classList.toggle('badge--success', match.available);
      availabilityEl.classList.toggle('badge--critical', !match.available);
    }
    if (submitBtn) {
      submitBtn.toggleAttribute('disabled', !match.available);
      submitBtn.textContent = match.available ? submitBtn.dataset.addLabel : submitBtn.dataset.soldOutLabel;
    }

    if (match.featured_media_src) {
      const gallery = document.querySelector('product-gallery [data-gallery-main] img');
      if (gallery) gallery.src = match.featured_media_src;
    }
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------------------------------------------------------------------
 * Shared money formatter — mirrors Shopify's own `money_format` token
 * substitution (the same tokens Shopify Admin lets a merchant configure:
 * amount / amount_no_decimals / amount_with_comma_separator / etc.), so
 * client-side-rendered prices (recently-viewed, wishlist) match
 * server-rendered `| money` output exactly rather than guessing a
 * hardcoded "$" format. Real format string comes from
 * `window.NKAMARO_STRINGS.moneyFormat` (`shop.money_format`, embedded in
 * theme.liquid — EMAIL 306 fix).
 * ------------------------------------------------------------------- */
function formatMoney(cents) {
  const format = (window.NKAMARO_STRINGS && window.NKAMARO_STRINGS.moneyFormat) || '${{amount}}';
  const value = cents / 100;
  function withDelimiters(decimals, thousandsSep, decimalSep) {
    const parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return decimals > 0 ? parts.join(decimalSep) : parts[0];
  }
  const replacements = {
    amount: withDelimiters(2, ',', '.'),
    amount_no_decimals: withDelimiters(0, ',', '.'),
    amount_with_comma_separator: withDelimiters(2, '.', ','),
    amount_no_decimals_with_comma_separator: withDelimiters(0, '.', ','),
    amount_with_space_separator: withDelimiters(2, ' ', '.'),
    amount_no_decimals_with_space_separator: withDelimiters(0, ' ', ''),
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (key in replacements ? replacements[key] : match));
}

/* ---------------------------------------------------------------------
 * Shared client-side product card renderer — fetches real product data
 * via Shopify's own `/products/{handle}.js` JSON endpoint (the same
 * real storefront data server-rendered cards use, just fetched instead
 * of Liquid-rendered) and builds markup matching
 * snippets/product-card.liquid closely enough to look native. Used by
 * both `recentlyViewed` and `wishlist` below (EMAIL 306 fix — neither
 * had a real renderer before; the wishlist toggle worked but a customer
 * could never see their list, and the recently-viewed slot existed in
 * markup but nothing ever painted into it).
 * ------------------------------------------------------------------- */
async function fetchProductCards(handles, excludeHandle, limit = 8) {
  const wanted = handles.filter((h) => h !== excludeHandle).slice(0, limit);
  const results = await Promise.all(
    wanted.map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ),
  );
  return results.filter(Boolean);
}

function renderProductCardsInto(container, products, removeLabel) {
  if (!container) return;
  container.innerHTML = '';
  products.forEach((product) => {
    const image = product.featured_image || (product.images && product.images[0]);
    const onSale = product.compare_at_price > product.price;
    const card = document.createElement('div');
    card.className = 'product-card';
    card.innerHTML = `
      <a href="${product.url}" class="product-card__media">
        ${image ? `<img src="${image}" alt="${(product.featured_image_alt || product.title).replace(/"/g, '&quot;')}" width="600" height="600" loading="lazy">` : '<div class="product-card__media-empty skeleton"></div>'}
        ${onSale ? `<span class="product-card__badge">${window.NKAMARO_STRINGS.saleLabel}</span>` : ''}
      </a>
      <a href="${product.url}" class="product-card__title t-body-sm"></a>
      <div class="product-card__price t-price">
        ${onSale ? `<span class="is-compare">${formatMoney(product.compare_at_price)}</span><span class="is-sale">${formatMoney(product.price)}</span>` : `<span>${formatMoney(product.price)}</span>`}
      </div>
      ${!product.available ? `<span class="t-caption">${window.NKAMARO_STRINGS.soldOutLabel}</span>` : ''}
      ${removeLabel ? `<button type="button" class="btn--ghost t-caption" data-wishlist-remove="${product.handle}">${removeLabel}</button>` : ''}
    `;
    // Set via textContent, not innerHTML, so a product title can never inject markup.
    card.querySelector('.product-card__title').textContent = product.title;
    container.appendChild(card);
  });
}

/* ---------------------------------------------------------------------
 * Recently viewed — localStorage, no server round-trip for tracking.
 * Records on PDP load; renders real product cards (fetched live) into
 * any [data-recently-viewed-slot] on the page, excluding the product
 * currently being viewed.
 * ------------------------------------------------------------------- */
(function recentlyViewed() {
  const STORAGE_KEY = 'nkamaro:recently-viewed';
  const MAX_ITEMS = 12;
  const current = document.querySelector('[data-record-recently-viewed]');
  const currentHandle = current ? current.dataset.recordRecentlyViewed : null;

  function getList() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  if (current && currentHandle) {
    const list = [currentHandle, ...getList().filter((h) => h !== currentHandle)].slice(0, MAX_ITEMS);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  const slot = document.querySelector('[data-recently-viewed-slot]');
  const grid = document.querySelector('[data-recently-viewed-grid]');
  if (!slot || !grid) return;

  fetchProductCards(getList(), currentHandle).then((products) => {
    if (products.length === 0) return;
    renderProductCardsInto(grid, products);
    slot.hidden = false;
  });
})();

/* ---------------------------------------------------------------------
 * Sticky Add-to-Cart — appears once the main product form's button
 * scrolls out of view (down the page), mirrors its label/disabled state,
 * and submits the same real form rather than duplicating cart logic.
 * ------------------------------------------------------------------- */
(function stickyAddToCart() {
  const bar = document.querySelector('[data-sticky-atc]');
  const mainButton = document.querySelector('[data-main-atc-button]');
  const stickyButton = document.querySelector('[data-sticky-atc-button]');
  if (!bar || !mainButton || !stickyButton) return;

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 },
    );
    observer.observe(mainButton);
  }

  stickyButton.addEventListener('click', () => {
    mainButton.closest('form')?.requestSubmit();
  });

  // Keep the sticky button's enabled/label state in sync with variant changes.
  const mutationObserver = new MutationObserver(() => {
    stickyButton.disabled = mainButton.disabled;
    stickyButton.textContent = mainButton.textContent;
  });
  mutationObserver.observe(mainButton, { attributes: true, childList: true, characterData: true, subtree: true });
})();

/* ---------------------------------------------------------------------
 * GA4 view_item — fires only if analytics is configured (window.dataLayer
 * exists, set up conditionally in theme.liquid) and real product data is
 * present on the page.
 * ------------------------------------------------------------------- */
(function trackViewItem() {
  if (!window.dataLayer) return;
  const script = document.querySelector('[data-analytics-view-item]');
  if (!script) return;
  try {
    const data = JSON.parse(script.textContent);
    window.dataLayer.push({ event: 'view_item', ecommerce: data });
  } catch {
    /* malformed data — never let analytics break the page */
  }
})();

/* ---------------------------------------------------------------------
 * Wishlist — client-side (localStorage) until/unless a real wishlist app
 * is installed for cross-device sync. Genuinely functional, not a stub:
 * persists across sessions on the same browser, reflected in the heart
 * icon's pressed state on load, a real header count badge, and a real
 * drawer (wishlist-drawer.liquid) to view/remove saved items — EMAIL 306
 * fix: the toggle always worked, but nothing ever let a customer see
 * their list.
 * ------------------------------------------------------------------- */
const wishlistStore = (function wishlistStoreFactory() {
  const STORAGE_KEY = 'nkamaro:wishlist';
  const listeners = [];

  function getList() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function setList(list) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    listeners.forEach((fn) => fn(list));
  }

  function toggle(handle) {
    const list = getList();
    setList(list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle]);
  }

  function remove(handle) {
    setList(getList().filter((h) => h !== handle));
  }

  function onChange(fn) {
    listeners.push(fn);
  }

  return { getList, toggle, remove, onChange };
})();

(function wishlistToggleButtons() {
  function paint() {
    const list = wishlistStore.getList();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(list.includes(btn.dataset.wishlistToggle)));
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((countEl) => {
      countEl.textContent = String(list.length);
      // A bare badge (the header icon) hides itself at zero; a labeled
      // line (the account dashboard card) instead defers to its own
      // sibling `[data-wishlist-count-line]`/`[data-wishlist-count-fallback]`
      // pair just below, so both states are never shown at once.
      const line = countEl.closest('[data-wishlist-count-line]');
      if (line) return;
      countEl.hidden = list.length === 0;
    });
    document.querySelectorAll('[data-wishlist-count-line]').forEach((line) => {
      line.hidden = list.length === 0;
      const fallback = line.parentElement?.querySelector('[data-wishlist-count-fallback]');
      if (fallback) fallback.hidden = list.length > 0;
    });
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    wishlistStore.toggle(btn.dataset.wishlistToggle);
  });

  wishlistStore.onChange(paint);
  paint();
})();

class WishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.closeBtn = this.querySelector('[data-wishlist-close]');
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) wishlistStore.remove(removeBtn.dataset.wishlistRemove);
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-wishlist-trigger]')) {
        e.preventDefault();
        this.open();
      }
    });
    wishlistStore.onChange(() => {
      if (this.hasAttribute('open')) this.render();
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.render();
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  async render() {
    const handles = wishlistStore.getList();
    if (handles.length === 0) {
      this.grid.innerHTML = '';
      this.emptyState.hidden = false;
      return;
    }
    this.emptyState.hidden = true;
    const products = await fetchProductCards(handles, null, 24);
    renderProductCardsInto(this.grid, products, window.NKAMARO_STRINGS.wishlistRemoveLabel);
  }
}
customElements.define('wishlist-drawer', WishlistDrawer);

/* ---------------------------------------------------------------------
 * Product comparison — same architecture as the wishlist above:
 * client-side (localStorage), no backend, real product data fetched live
 * via Shopify's own `/products/{handle}.js` endpoint. Capped at 4 items
 * (a 5th+ column stops being readable on real screens). A sticky bottom
 * bar (compare-bar.liquid) appears once 1+ products are selected; its
 * "Compare" button opens a real side-by-side table — price, availability,
 * vendor, type, and every real variant option (Size/Color/etc.) read
 * straight from each product's own `options` array, never fabricated
 * "features".
 * ------------------------------------------------------------------- */
const MAX_COMPARE_ITEMS = 4;

const compareStore = (function compareStoreFactory() {
  const STORAGE_KEY = 'nkamaro:compare';
  const listeners = [];

  function getList() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function setList(list) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    listeners.forEach((fn) => fn(list));
  }

  function toggle(handle) {
    const list = getList();
    if (list.includes(handle)) {
      setList(list.filter((h) => h !== handle));
      return true;
    }
    if (list.length >= MAX_COMPARE_ITEMS) return false;
    setList([...list, handle]);
    return true;
  }

  function remove(handle) {
    setList(getList().filter((h) => h !== handle));
  }

  function clear() {
    setList([]);
  }

  function onChange(fn) {
    listeners.push(fn);
  }

  return { getList, toggle, remove, clear, onChange };
})();

(function compareToggleButtons() {
  function paint() {
    const list = compareStore.getList();
    document.querySelectorAll('[data-compare-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(list.includes(btn.dataset.compareToggle)));
    });
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-compare-toggle]');
    if (!btn) return;
    const added = compareStore.toggle(btn.dataset.compareToggle);
    if (!added) {
      const bar = document.querySelector('compare-bar');
      bar?.flashLimit();
    }
  });

  compareStore.onChange(paint);
  paint();
})();

async function fetchFullProducts(handles) {
  const results = await Promise.all(
    handles.map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ),
  );
  return results.filter(Boolean);
}

class CompareBar extends HTMLElement {
  connectedCallback() {
    this.strip = this.querySelector('[data-compare-strip]');
    this.countEl = this.querySelector('[data-compare-count]');
    this.limitEl = this.querySelector('[data-compare-limit]');
    this.openBtn = this.querySelector('[data-compare-open]');
    this.clearBtn = this.querySelector('[data-compare-clear]');

    this.openBtn?.addEventListener('click', () => {
      document.querySelector('compare-drawer')?.open();
    });
    this.clearBtn?.addEventListener('click', () => compareStore.clear());
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-compare-strip-remove]');
      if (removeBtn) compareStore.remove(removeBtn.dataset.compareStripRemove);
    });

    compareStore.onChange(() => this.render());
    this.render();
  }

  flashLimit() {
    if (!this.limitEl) return;
    this.limitEl.hidden = false;
    window.clearTimeout(this._limitTimer);
    this._limitTimer = window.setTimeout(() => {
      this.limitEl.hidden = true;
    }, 2400);
  }

  async render() {
    const handles = compareStore.getList();
    this.toggleAttribute('open', handles.length > 0);
    if (this.countEl) this.countEl.textContent = String(handles.length);
    if (this.openBtn) this.openBtn.disabled = handles.length < 2;
    if (!this.strip || handles.length === 0) return;

    const products = await fetchFullProducts(handles);
    this.strip.innerHTML = '';
    products.forEach((product) => {
      const image = product.featured_image || (product.images && product.images[0]);
      const item = document.createElement('div');
      item.className = 'compare-bar__item';
      item.innerHTML = `
        ${image ? `<img src="${image}" alt="" width="44" height="44" loading="lazy">` : '<div class="compare-bar__item-empty skeleton"></div>'}
        <button type="button" class="compare-bar__item-remove" data-compare-strip-remove="${product.handle}" aria-label="${window.NKAMARO_STRINGS.compareRemoveLabel}">&times;</button>
      `;
      this.strip.appendChild(item);
    });
  }
}
customElements.define('compare-bar', CompareBar);

class CompareDrawer extends HTMLElement {
  connectedCallback() {
    this.closeBtn = this.querySelector('[data-compare-close]');
    this.table = this.querySelector('[data-compare-table]');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
      const removeBtn = e.target.closest('[data-compare-table-remove]');
      if (removeBtn) compareStore.remove(removeBtn.dataset.compareTableRemove);
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    compareStore.onChange(() => {
      if (this.hasAttribute('open')) this.render();
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.render();
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  async render() {
    const handles = compareStore.getList();
    if (!this.table) return;
    if (handles.length === 0) {
      this.table.replaceChildren();
      this.close();
      return;
    }

    const products = await fetchFullProducts(handles);
    const strings = window.NKAMARO_STRINGS;

    // Every real, universally-available Shopify product field — never a
    // fabricated "feature list". Variant option rows (Size/Color/etc.)
    // are appended dynamically per-product below since option sets vary.
    // Product title/vendor/type/option values are merchant-entered data,
    // not trusted markup — every one of them is set via `.textContent`
    // below, never interpolated into an HTML string (same discipline
    // `renderProductCardsInto()` already applies to product titles).
    const optionNames = Array.from(new Set(products.flatMap((p) => (p.options || []).map((o) => o.name))));

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(this.headerCell(strings.compareHeading));
    products.forEach((product) => {
      const th = document.createElement('th');
      th.scope = 'col';
      const link = document.createElement('a');
      link.href = product.url;
      link.className = 't-body-sm';
      link.textContent = product.title;
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'btn--ghost t-caption compare-table__remove';
      removeBtn.dataset.compareTableRemove = product.handle;
      removeBtn.textContent = strings.compareRemoveLabel;
      th.append(link, removeBtn);
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);

    const tbody = document.createElement('tbody');
    const addRow = (label, cellFor) => {
      const tr = document.createElement('tr');
      tr.appendChild(this.headerCell(label, true));
      products.forEach((product) => tr.appendChild(cellFor(product)));
      tbody.appendChild(tr);
    };
    const textCell = (value) => {
      const td = document.createElement('td');
      td.textContent = value;
      return td;
    };

    addRow(strings.compareRowImage, (p) => {
      const td = document.createElement('td');
      if (p.featured_image) {
        const img = document.createElement('img');
        img.src = p.featured_image;
        img.alt = '';
        img.width = 120;
        img.height = 120;
        img.loading = 'lazy';
        td.appendChild(img);
      }
      return td;
    });
    addRow(strings.compareRowTitle, (p) => textCell(p.title));
    addRow(strings.compareRowPrice, (p) => {
      const td = document.createElement('td');
      if (p.compare_at_price > p.price) {
        const was = document.createElement('span');
        was.className = 'is-compare';
        was.textContent = formatMoney(p.compare_at_price);
        const now = document.createElement('span');
        now.className = 'is-sale';
        now.textContent = formatMoney(p.price);
        td.append(was, ' ', now);
      } else {
        td.textContent = formatMoney(p.price);
      }
      return td;
    });
    addRow(strings.compareRowAvailability, (p) => textCell(p.available ? strings.compareInStock : strings.compareOutOfStock));
    addRow(strings.compareRowVendor, (p) => textCell(p.vendor || '—'));
    addRow(strings.compareRowType, (p) => textCell(p.type || '—'));
    optionNames.forEach((name) => {
      addRow(name, (p) => {
        const opt = (p.options || []).find((o) => o.name === name);
        return textCell(opt ? opt.values.join(', ') : '—');
      });
    });

    this.table.replaceChildren(thead, tbody);
  }

  headerCell(label, isRow) {
    const th = document.createElement('th');
    th.scope = isRow ? 'row' : 'col';
    if (isRow) th.className = 't-caption';
    th.textContent = label;
    return th;
  }
}
customElements.define('compare-drawer', CompareDrawer);

/* ---------------------------------------------------------------------
 * Login page — reveal the password-recovery form in place of scroll,
 * matching Shopify's standard customer_login/recover_customer_password
 * form pairing on one page.
 * ------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-toggle-recover]');
  if (!trigger) return;
  event.preventDefault();
  const recoverForm = document.getElementById('RecoverForm');
  const loginForm = trigger.closest('form');
  if (!recoverForm) return;
  recoverForm.style.display = recoverForm.style.display === 'none' || !recoverForm.style.display ? 'flex' : 'none';
  recoverForm.style.flexDirection = 'column';
  if (loginForm) loginForm.hidden = recoverForm.style.display !== 'none';
});

/* ---------------------------------------------------------------------
 * Accordion — FAQ, product details, mobile footer.
 * ------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-accordion-trigger]');
  if (!trigger) return;
  const item = trigger.closest('[data-accordion-item]');
  const panel = item?.querySelector('[data-accordion-panel]');
  if (!panel) return;
  const isOpen = item.getAttribute('open') === '';
  item.toggleAttribute('open', !isOpen);
  trigger.setAttribute('aria-expanded', String(!isOpen));
});

/* ---------------------------------------------------------------------
 * Digital Downloads — EMAIL 326 §3 "Customer Download Center". Fetches
 * from NKAMARO's backend via Shopify's own App Proxy: a relative fetch
 * to the shop's own domain, which Shopify's edge intercepts, verifies
 * came from this storefront, signs, and forwards — the browser never
 * talks to NKAMARO's backend directly and never handles a secret. Real
 * data only: no grants renders nothing (not a fake empty state message
 * unless the fetch genuinely succeeded with zero results), and a 404
 * (App Proxy not yet activated in the Partner Dashboard — a real
 * Founder action, not a bug) leaves the whole section hidden rather
 * than showing a broken error for a feature not turned on yet.
 * ------------------------------------------------------------------- */
(function digitalDownloads() {
  const section = document.querySelector('[data-digital-downloads]');
  const list = document.querySelector('[data-digital-downloads-list]');
  if (!section || !list) return;

  const basePath = section.dataset.appProxyPath;
  if (!basePath) return;

  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString(document.documentElement.lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  }

  function renderGrant(grant) {
    const strings = window.NKAMARO_STRINGS;
    const card = document.createElement('div');
    card.className = 'digital-downloads__card surface';
    card.dataset.grantId = grant.id;

    const title = document.createElement('p');
    title.className = 't-body';
    title.textContent = grant.nkamaroProductRef;
    card.appendChild(title);

    const meta = document.createElement('p');
    meta.className = 't-caption';
    const remaining = Math.max(0, grant.maxDownloads - grant.downloadCount);
    const metaParts = [
      strings.downloadsRemainingTemplate.replace('{{ count }}', String(remaining)),
      strings.fileCountTemplate.replace('{{ count }}', String(grant.fileCount)),
      strings.expiresLabelTemplate.replace('{{ date }}', formatDate(grant.expiresAt)),
    ];
    meta.textContent = metaParts.join(' · ');
    card.appendChild(meta);

    if (grant.licenseKey) {
      const licenseRow = document.createElement('div');
      licenseRow.className = 'digital-downloads__license cluster';
      licenseRow.style.setProperty('--gap', 'var(--space-1)');
      const licenseLabel = document.createElement('span');
      licenseLabel.className = 't-caption';
      licenseLabel.textContent = `${strings.licenseKeyLabel}: `;
      const licenseValue = document.createElement('code');
      licenseValue.className = 't-caption';
      licenseValue.textContent = grant.licenseKey;
      const copyBtn = document.createElement('button');
      copyBtn.type = 'button';
      copyBtn.className = 'btn--ghost t-caption';
      copyBtn.textContent = strings.copyLicense;
      copyBtn.addEventListener('click', () => {
        navigator.clipboard?.writeText(grant.licenseKey).then(() => {
          copyBtn.textContent = strings.copiedLicense;
          window.setTimeout(() => (copyBtn.textContent = strings.copyLicense), 2000);
        });
      });
      licenseRow.append(licenseLabel, licenseValue, copyBtn);
      card.appendChild(licenseRow);
    }

    const actionRow = document.createElement('div');
    actionRow.className = 'digital-downloads__actions';

    if (grant.revoked) {
      const notice = document.createElement('p');
      notice.className = 't-caption';
      notice.textContent = strings.downloadRevoked;
      actionRow.appendChild(notice);
    } else if (remaining <= 0) {
      const notice = document.createElement('p');
      notice.className = 't-caption';
      notice.textContent = strings.downloadLimitReached;
      actionRow.appendChild(notice);
    } else {
      const downloadBtn = document.createElement('button');
      downloadBtn.type = 'button';
      downloadBtn.className = 'btn btn--primary btn--sm';
      downloadBtn.textContent = strings.downloadButton;
      downloadBtn.addEventListener('click', () => redeemGrant(grant, downloadBtn, actionRow));
      actionRow.appendChild(downloadBtn);
    }
    card.appendChild(actionRow);

    return card;
  }

  async function redeemGrant(grant, button, actionRow) {
    const strings = window.NKAMARO_STRINGS;
    button.disabled = true;
    button.textContent = strings.downloading;
    try {
      const response = await fetch(`${basePath}/redeem?redemption_token=${encodeURIComponent(grant.redemptionToken)}`, { method: 'POST' });
      if (!response.ok) throw new Error('redeem failed');
      const result = await response.json();
      const linksWrap = document.createElement('div');
      linksWrap.className = 'digital-downloads__links cluster';
      linksWrap.style.setProperty('--gap', 'var(--space-1)');
      result.downloadUrls.forEach((url, i) => {
        const link = document.createElement('a');
        link.href = url;
        link.className = 'btn btn--secondary btn--sm';
        link.textContent = result.downloadUrls.length > 1 ? `${strings.downloadButton} ${i + 1}` : strings.downloadButton;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        linksWrap.appendChild(link);
      });
      actionRow.replaceChildren(linksWrap);
    } catch {
      button.disabled = false;
      button.textContent = strings.downloadsError;
    }
  }

  fetch(basePath)
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`status ${response.status}`))))
    .then((grants) => {
      if (!Array.isArray(grants) || grants.length === 0) return;
      grants.forEach((grant) => list.appendChild(renderGrant(grant)));
      section.hidden = false;
      document.querySelector('[data-digital-downloads-card]')?.removeAttribute('hidden');
    })
    .catch(() => {
      // App Proxy not yet activated, or genuinely no grants — either way,
      // stay hidden rather than show a broken section.
    });
})();

(function giftCardCopy() {
  const button = document.querySelector('[data-gift-card-copy]');
  if (!button) return;

  button.addEventListener('click', async () => {
    const value = button.getAttribute('data-copy-value') || '';
    const original = button.textContent;
    try {
      await navigator.clipboard.writeText(value);
      button.textContent = button.getAttribute('data-copied-label') || original;
      setTimeout(() => {
        button.textContent = original;
      }, 2000);
    } catch {
      // Clipboard API unavailable (non-HTTPS/older browser) — the code is
      // already visible as selectable text, so this is a silent no-op
      // rather than a broken error state.
    }
  });
})();
